import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Add, Delete, Edit, Cancel, Done } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  Checkbox,
  FormControl,
} from '@mui/material';
import { TwoActionsDialogService } from '../../../services/twoActionsDialog/twoActionsDialogService';
import { FadingTooltip } from '../../../ui';
import { WizardUtils, DEFAULT_NUMERIC_OPERATOR } from '../../../utils/WizardUtils';
import { TableCellComponent } from './TableCellComponent';

const labels = {
  ajouterUneLigne: 'Ajouter une ligne',
};

const EditableTable = ({
  id,
  columns,
  rows,
  editMode,
  inlineEdit,
  onChange,
  notFilteredRows,
  filteringCol,
  onDeleteRow,
  confirmDeleteRow,
  confirmDeleteRowDialogProps,
  onNewRowSaved,
  selectedAssetClassAttrsList,
  canAddDeleteRows,
  addButtonAlignment,
  onRowUpdate,
  referentielConsequences,
  hideActions,
  onValueCopyChange,
  tripleInput,
}) => {
  const [editIdx, setEditIdx] = useState(-1);
  const [beingEdited, setBeingEdited] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [isNewRow, setIsNewRow] = useState(false);
  const [localRows, setLocalRows] = useState([]);

  useEffect(() => {
    if (!(editIdx !== -1 && isNewRow)) {
      setLocalRows(rows);
    }
    if (editIdx !== -1 && !isNewRow) {
      const stillExists = rows?.some((r) => r.id === currentRow?.id);
      if (!stillExists) {
        setEditIdx(-1);
        setIsNewRow(false);
        setCurrentRow({});
        setBeingEdited(false);
      }
    }
  }, [rows, isNewRow, editIdx, currentRow.id]);

  const rowsList = useMemo(() => {
    return notFilteredRows ?? rows;
  }, [notFilteredRows, rows]);

  const handleEdit = (idx) => {
    if (editIdx !== -1 && !isRowValid(rows[editIdx])) {
      return;
    }
    setEditIdx(idx);
    setBeingEdited(true);
    setCurrentRow(rows[idx]);
    setIsNewRow(false);
  };

  const handleSave = () => {
    if (!isRowValid(currentRow)) return;
    const index = rowsList.findIndex((row) => row.id === currentRow.id);
    if (index === -1) {
      onChange([...rowsList, currentRow]);
      if (isNewRow && onNewRowSaved != null) {
        onNewRowSaved(currentRow);
      }
    } else {
      const oldRow = { ...rowsList[index] };
      const valueCopy = [...rowsList];
      valueCopy[index] = { ...currentRow };
      onChange(valueCopy);

      if (onValueCopyChange) {
        onValueCopyChange(valueCopy);
      }

      if (onRowUpdate) {
        onRowUpdate(currentRow, oldRow);
      }
    }
    setEditIdx(-1);
    setIsNewRow(false);
    setBeingEdited(false);
  };

  const handleCancel = () => {
    if (isNewRow) {
      setLocalRows(localRows.filter((row) => row.id !== currentRow.id));
    }
    setEditIdx(-1);
    setIsNewRow(false);
    setBeingEdited(false);
  };

  const handleChange = (column, value) => {
    const field = column.field;
    const currentValue = currentRow[field];
    if (currentValue === value) return;
    if (column && column.inputType === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        if (column.min !== undefined && numValue < column.min) {
          value = column.min;
        }
        if (column.max !== undefined && numValue > column.max) {
          value = column.max;
        }
      }
    }

    const updatedRow = { ...currentRow, [field]: value };

    if (field === 'AttributDeclencheur') {
      const operateurColumn = columns.find((col) => col.field === 'Operateur');
      if (operateurColumn) {
        const previousAttribute = currentValue;
        const isPreviousNumeric = WizardUtils.isNumericComparingAttribute(previousAttribute);
        const isNewNumeric = WizardUtils.isNumericComparingAttribute(value);

        if (isNewNumeric) {
          if (!isPreviousNumeric || !updatedRow.Operateur) {
            updatedRow.Operateur = DEFAULT_NUMERIC_OPERATOR;
          }
        } else if (isPreviousNumeric) {
          updatedRow.Operateur = '';
        }
      }
      updatedRow.ValeurReference = '';
    }

    setCurrentRow(updatedRow);
  };

  const handleInlineChange = (rowIndex, column, value) => {
    const field = column.field;
    let newValue = value;
    if (column && column.inputType === 'number') {
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue)) {
        if (column.min !== undefined && numValue < column.min) newValue = column.min;
        if (column.max !== undefined && numValue > column.max) newValue = column.max;
      }
    }
    const targetId = localRows[rowIndex]?.id;

    setLocalRows((prev) => {
      const copy = [...prev];
      copy[rowIndex] = { ...copy[rowIndex], [field]: newValue };
      return copy;
    });

    const mergedAll = (rowsList || []).map((r) => (r.id === targetId ? { ...r, [field]: newValue } : r));
    onChange(mergedAll);
  };

  const handleAddRow = () => {
    if (editMode) {
      const newRow = { id: generateUniqueId('r') };
      if (filteringCol) newRow[filteringCol?.field] = filteringCol?.value;
      columns.forEach((col) => {
        if (col.inputType !== 'button' && col.withButton !== true) {
          if (col.defaultValue !== undefined) {
            newRow[col.field] = col.defaultValue;
          } else if (col.getDefaultValue) {
            newRow[col.field] = col.getDefaultValue(rows);
          } else {
            newRow[col.field] = col.inputType === 'checkbox' ? false : '';
          }
        }
      });

      setLocalRows([...localRows, newRow]);
      setEditIdx(rows.length);
      setCurrentRow(newRow);
      setIsNewRow(true);
      setBeingEdited(true);
    }
  };

  const handleDeleteRow = async (event, id) => {
    let result = 2;
    if (confirmDeleteRow) {
      event.stopPropagation();
      result = await TwoActionsDialogService.openDialog(confirmDeleteRowDialogProps);
    }
    if (result === 2) {
      if (onDeleteRow) {
        const rowToDelete = rowsList.find((row) => row.id === id);
        onDeleteRow(rowToDelete);
      }
      onChange(rowsList.filter((row) => row.id !== id));
    }
  };

  const generateUniqueId = (type) => {
    let newId;
    do {
      const randomString = Math.random().toString(36).substring(2, 15);
      newId = `${type}-${randomString}`;
    } while (rows.some((row) => row.id === newId));
    return newId;
  };

  const isRowValid = (row) => {
    if (row == null || typeof row !== 'object') {
      return false;
    }
    for (const col of columns) {
      if (col.required && col.field) {
        const value = row[col.field];
        if (value == null || (typeof value === 'string' && value.trim() === '')) {
          return false;
        }
      }
      if (col.valid && typeof col.valid === 'function') {
        if (col.valid(row, rowsList)) {
          return false;
        }
      }
    }
    return true;
  };

  const isCellDisabled = useCallback(
    (col) => {
      let error = false;
      if (col.valid && typeof col.valid === 'function') {
        error = col.valid(currentRow, rowsList);
      }
      if (!error) return typeof col.disabled === 'function' ? col.disabled(currentRow) : col.disabled;
      return false;
    },
    [currentRow, rowsList]
  );

  const isCellValid = useCallback(
    (col) => {
      if (typeof col.valid === 'function') return col.valid(currentRow, rowsList);
    },
    [currentRow, rowsList]
  );

  hideActions = hideActions || inlineEdit;
  const totalCols = columns.length + (hideActions ? 0 : 1);

  const getColWidth = useMemo(() => {
    const shouldUseMultiInputField =
      tripleInput && beingEdited && !currentRow.AttributDeclencheur?.startsWith('CustomDataString');
    return (index, isActionsColumn = false) => {
      if (shouldUseMultiInputField) {
        const TRIPLE_INPUT_COL_WIDTH = 30;
        const REMAINING_WIDTH = 100 - TRIPLE_INPUT_COL_WIDTH;

        const secondLastIndex = columns.length - 1;
        const numOtherCols = totalCols - 1;
        const otherColWidth = REMAINING_WIDTH / numOtherCols;

        if (!isActionsColumn && index === secondLastIndex) {
          return `${TRIPLE_INPUT_COL_WIDTH}%`;
        }
        return `${otherColWidth}%`;
      }
      return `${100 / totalCols}%`;
    };
  }, [tripleInput, beingEdited, columns.length, totalCols, currentRow.AttributDeclencheur]);

  const getCellValue = (col, row) => {
    const value = row[col.field];
    if (col.inputType === 'multiSelect' && Array.isArray(value)) {
      return value
        .map((v) => {
          const option = col.options?.find((opt) => (opt.value ? opt.value === v : opt === v));
          return option ? option.label || option : v;
        })
        .join(', ');
    }
    if (col.options && Array.isArray(col.options)) {
      const option = col.options.find((opt) => (opt.value ? opt.value === value : opt === value));
      if (option) return option.label || option;
    }
    return value;
  };

  return (
    <Box sx={{ marginTop: 2 }}>
      <TableContainer>
        <Table
          data-cy={`${id}-table`}
          size="small"
          padding="none"
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: inlineEdit ? 'none' : undefined,
            },
          }}
        >
          <TableHead>
            <TableRow key="header" sx={{ display: 'flex' }}>
              {columns.map((col, colIndex) => (
                <TableCell
                  key={col.field}
                  padding="none"
                  sx={{
                    width: getColWidth(colIndex),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {col.label}&nbsp;
                    {col.info && (
                      <FadingTooltip title={col.info ?? ''} useSpan={true}>
                        <InfoOutlinedIcon color="inherit" fontSize="inherit" sx={{ display: 'inherit' }} />
                      </FadingTooltip>
                    )}
                    {col.required && <span style={{ color: 'red' }}>*</span>}
                  </Typography>
                </TableCell>
              ))}
              {!hideActions && (
                <TableCell
                  sx={{
                    width: getColWidth(columns.length, true),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>{'Actions'}</Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody data-cy={`${id}-table-body`}>
            {localRows?.map((row, idx) => (
              <TableRow key={row.id} row-index={idx} sx={{ display: 'flex', alignContent: 'center' }}>
                {columns.map((col, colIndex) => (
                  <TableCell
                    data-cy={col.field}
                    key={col.field}
                    align="center"
                    size="small"
                    sx={{
                      display: col.inputType === 'checkbox' || col.withButton ? 'flex' : 'inline-block',
                      alignItems: col.inputType === 'checkbox' || col.withButton ? 'center' : 'initial',
                      justifyContent: col.inputType === 'checkbox' || col.withButton ? 'center' : 'initial',
                      width: getColWidth(colIndex),
                      px: 0.5,
                      py: inlineEdit ? 0.5 : 0,
                    }}
                  >
                    {col.inputType === 'button' ? (
                      <Button
                        onClick={() => col.onClick(row?.IdentifiantAction)}
                        disabled={!row?.IdentifiantAction || editIdx !== -1}
                      >
                        {col.buttonLabel}
                      </Button>
                    ) : col.withButton && editIdx !== idx ? (
                      <FadingTooltip
                        useSpan={true}
                        title={row[col.field] || ''}
                        placement="bottom"
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, 20],
                                },
                              },
                            ],
                          },
                        }}
                      >
                        <Button
                          onClick={() => {
                            const actionId = row?.IdentifiantAction || row?.id || '';
                            if (actionId && typeof actionId === 'string' && actionId.trim() !== '') {
                              col.onClick(actionId);
                            }
                          }}
                          disabled={!(row?.IdentifiantAction || row?.id) || editIdx !== -1}
                        >
                          {col.buttonLabel} ({row[col.field] ? row[col.field].split('|').length : 0})
                        </Button>
                      </FadingTooltip>
                    ) : col.inputType === 'checkbox' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          height: '100%',
                        }}
                      >
                        <Checkbox
                          size="medium"
                          sx={{ p: 0, m: 0 }}
                          checked={
                            editIdx !== idx
                              ? WizardUtils.isValueTruthy(row[col.field])
                              : WizardUtils.isValueTruthy(currentRow[col.field])
                          }
                          onChange={(e) =>
                            inlineEdit
                              ? handleInlineChange(idx, col, e.target.checked)
                              : handleChange(col, e.target.checked)
                          }
                          disabled={!inlineEdit && editIdx !== idx}
                        />
                      </Box>
                    ) : inlineEdit || editIdx === idx ? (
                      <FormControl
                        sx={{ width: '100%', justifyItems: 'center' }}
                        disabled={isCellDisabled(col)}
                        error={isCellValid(col)}
                      >
                        {(() => {
                          const tableCellComponent = (
                            <TableCellComponent
                              col={col}
                              handleChange={(column, value) =>
                                inlineEdit ? handleInlineChange(idx, column, value) : handleChange(column, value)
                              }
                              currentRow={inlineEdit ? row : currentRow}
                              isCellDisabled={isCellDisabled}
                              isCellValid={isCellValid}
                              selectedAssetClassAttrsList={selectedAssetClassAttrsList}
                              referentielConsequences={referentielConsequences}
                            />
                          );
                          const isAutocompleteWithButton = col.inputType === 'autocomplete';
                          const isMultiSelect = col.inputType === 'multiSelect';
                          const isSelect = col.inputType === 'select';
                          const shouldHideTooltip =
                            (isAutocompleteWithButton || isMultiSelect || isSelect) && !inlineEdit && editIdx === idx;

                          return shouldHideTooltip ? (
                            tableCellComponent
                          ) : (
                            <FadingTooltip
                              useSpan={true}
                              title={
                                row[col.field]?.length > 10
                                  ? col.field === 'AttributDeclencheur'
                                    ? getCellValue(col, row)
                                    : row[col.field]
                                  : ''
                              }
                            >
                              {tableCellComponent}
                            </FadingTooltip>
                          );
                        })()}
                      </FormControl>
                    ) : (
                      <FadingTooltip
                        useSpan={true}
                        title={getCellValue(col, row)?.length > 10 ? getCellValue(col, row) : ''}
                      >
                        <Typography
                          sx={{
                            marginTop: 2.1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            maxWidth: WizardUtils.getMaxCharWidth(columns.length),
                            whiteSpace: 'nowrap',
                            // add unit at the end if exists
                            '&::after': {
                              content:
                                col.field === 'ValeurReference' &&
                                ['Downtime', 'InspectionDowntime'].includes(row.AttributDeclencheur?.toString())
                                  ? '" an(s)"'
                                  : '""',
                            },
                          }}
                        >
                          {col.field === 'AttributDeclencheur' ? getCellValue(col, row) : row[col.field]}
                        </Typography>
                      </FadingTooltip>
                    )}
                  </TableCell>
                ))}
                {!hideActions && (
                  <TableCell
                    data-cy="table-row-actions"
                    sx={{
                      display: 'inline-flex',
                      width: getColWidth(columns.length, true),
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {editIdx === idx ? (
                        <>
                          <IconButton data-cy="validate" onClick={handleSave} disabled={!isRowValid(currentRow)}>
                            <Done />
                          </IconButton>
                          <IconButton data-cy="cancel" onClick={handleCancel}>
                            <Cancel />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          {editMode && (
                            <IconButton data-cy="edit" onClick={() => handleEdit(idx)} disabled={editIdx !== -1}>
                              <Edit />
                            </IconButton>
                          )}
                          {editMode && canAddDeleteRows && (
                            <IconButton
                              data-cy="delete"
                              onClick={(event) => handleDeleteRow(event, row.id)}
                              disabled={editIdx !== -1}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ borderBottom: 0 }} colSpan={totalCols} align={addButtonAlignment}>
                {canAddDeleteRows && (
                  <Button
                    data-cy={`add-row-${id}`}
                    variant="contained"
                    color="secondary"
                    onClick={handleAddRow}
                    disabled={!editMode || editIdx !== -1}
                    sx={{ my: 0.5 }}
                  >
                    <Add />
                    {labels.ajouterUneLigne}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

EditableTable.propTypes = {
  id: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      inputType: PropTypes.oneOfType([
        PropTypes.oneOf([
          'text',
          'select',
          'autocomplete',
          'number',
          'checkbox',
          'multiSelect',
          'button',
          'nestedSelect',
        ]),
        PropTypes.func,
      ]),
      options: PropTypes.array,
      clearIfDisabled: PropTypes.bool,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  editMode: PropTypes.bool,
  inlineEdit: PropTypes.bool,
  onChange: PropTypes.func,
  notFilteredRows: PropTypes.array,
  filteringCol: PropTypes.object,
  onDeleteRow: PropTypes.func,
  confirmDeleteRow: PropTypes.bool,
  confirmDeleteRowDialogProps: PropTypes.object,
  onNewRowSaved: PropTypes.func,
  canAddDeleteRows: PropTypes.bool,
  addButtonAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  onRowUpdate: PropTypes.func,
  selectedAssetClassAttrsList: PropTypes.array,
  referentielConsequences: PropTypes.array,
  hideActions: PropTypes.bool,
  onValueCopyChange: PropTypes.func,
  tripleInput: PropTypes.bool,
};

EditableTable.defaultProps = {
  editMode: true,
  inlineEdit: false,
  confirmDeleteRow: false,
  confirmDeleteRowDialogProps: {},
  canAddDeleteRows: true,
  addButtonAlignment: 'right',
  hideActions: false,
  tripleInput: false,
};

export default EditableTable;
